import React from 'react';
import {
  Box, Button, styled, Typography, useTheme,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTranslation } from 'react-i18next';
import { useLogo } from '../config/useLogo';
import { TestIds } from '../assets/testIds';
import { WalletListType } from './enums';

interface WalletRewardsBalanceCardProps {
  hasRewardsBalance: boolean;
  rewardsBalanceValue: string;
  rewardsBalanceLastUpdate: string;
  disable?: boolean;
  canEditPayoutMethod?: boolean;
  optionalStyles?: React.CSSProperties;
  setModalType?: (event: WalletListType.PAYOUT |
  WalletListType.TRANSACTIONS |
  WalletListType.PARTNERSRELATION) => void;
}

export function WalletRewardsBalanceCard({
  hasRewardsBalance,
  rewardsBalanceValue,
  rewardsBalanceLastUpdate,
  setModalType,
  disable = false,
  canEditPayoutMethod = true,
  optionalStyles = {},
}: WalletRewardsBalanceCardProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { emptyWallet: EmptyWallet, walletRewards: WalletRewards } = useLogo();

  const RewardsBalanceButton = styled(Button)(
    () => ({
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: theme.spacing(1),
      padding: theme.spacing(2, 1, 2, 1.5),
      alignSelf: 'stretch',
      width: '100%',
      maxWidth: theme.spacing(60),
      maxHeight: theme.spacing(6.5),
      borderRadius: theme.spacing(3),
      fontWeight: 700,
      fontSize: theme.spacing(2),
      color: hasRewardsBalance ?
        theme.palette.brand.primary :
        theme.palette.common.white,
      backgroundColor: hasRewardsBalance ?
        theme.palette.brand.surface :
        theme.palette.brand.primary,
      '&:disabled': {
        opacity: 0.4,
        cursor: 'not-allowed',
      },
      '&:hover': {
        backgroundColor: hasRewardsBalance ?
          theme.palette.brand.surface :
          theme.palette.brand.primary,
        cursor: 'pointer',
      },
      [theme.breakpoints.down('sm')]: {
        fontSize: theme.spacing(1.75),
      },
    }),
  );

  const RewardsBalanceContent = () => {
    if (hasRewardsBalance) {
      return (
        <Box
          id='rewards-balance-content'
          sx={{
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography
              data-testid={TestIds.walletPage.rewardsBalanceValue}
              variant='h14'
              sx={{
                display: 'flex',
                alignSelf: 'center',
                lineHeight: theme.spacing(2.5),
                mb: '20px',
                [theme.breakpoints.down('sm')]: { fontSize: theme.spacing(3.75) },
              }}
            >
              ${rewardsBalanceValue}
            </Typography>
            <Typography
              id='reward-balance-value-last-update-label'
              variant='h15'
              sx={{
                lineHeight: theme.spacing(2.5),
                [theme.breakpoints.down('sm')]: { fontSize: theme.spacing(1.75) },
              }}
            >
              {t('wallet.lastUpdate')}
            </Typography>
            <Typography
              data-testid={TestIds.walletPage.rewardsBalanceLastUpdate}
              variant='body8'
              sx={{
                lineHeight: theme.spacing(2.5),
                [theme.breakpoints.down('sm')]: { fontSize: theme.spacing(1.75) },
              }}
            >
              {rewardsBalanceLastUpdate}
            </Typography>
          </Box>
          {WalletRewards && <WalletRewards />}
        </Box>
      );
    }

    return (
      <Box
        id='rewards-no-balance-content'
        sx={{
          display: 'flex',
          width: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          gap: theme.spacing(1.25),
        }}
      >
        {EmptyWallet && <EmptyWallet />}
        <Typography id='rewards-no-balance-note' variant='body1'>
          {t('wallet.rewardsNotEarningNotes')}
        </Typography>
      </Box>
    );
  };

  return (
    <Box
      data-testid={TestIds.walletPage.rewardsBalanceCard}
      sx={{
        display: 'flex',
        padding: theme.spacing(3.125),
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: theme.spacing(3.125),
        flex: '1 0 0',
        borderRadius: theme.spacing(1.5),
        color: hasRewardsBalance ?
          theme.palette.common.white :
          theme.palette.common.black,
        background: hasRewardsBalance ?
          theme.palette.brand.primary :
          theme.palette.common.white,
        border: `1px solid ${theme.palette.grey[200]}`,
        ...optionalStyles,
      }}
    >
      <Typography
        id='reward-balance-card-title'
        variant='h2'
        sx={{
          lineHeight: theme.spacing(3),
          [theme.breakpoints.down('sm')]: { fontSize: theme.spacing(2) },
        }}
      >
        {hasRewardsBalance ?
          t('wallet.rewardsBalanceTitle') :
          t('wallet.earningAndRewards')}
      </Typography>
      <RewardsBalanceContent />
      {hasRewardsBalance && canEditPayoutMethod &&
        <RewardsBalanceButton
          data-testid={TestIds.walletPage.rewardsBalanceBtn}
          onClick={() => { setModalType?.(WalletListType.PAYOUT); }}
          disabled={disable}
        >
          {t('wallet.linkPayout')}
          <ArrowForwardIcon />
        </RewardsBalanceButton>
      }
    </Box>
  );
}
